import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useDialog } from "@/context/DialogProvider";
import {
  Building2, Plus, Trash2, ChefHat, Archive, ShowerHead, Truck, Briefcase,
  MoreHorizontal, MapPin, Pencil, X as XIcon,
} from "lucide-react";
import StepLayout from "./StepLayout";

const CATEGORIES = [
  { value: "food_production", label: "Food production / Kitchen", icon: ChefHat },
  { value: "storage", label: "Storage / Cold room / Dry store", icon: Archive },
  { value: "restroom", label: "Restroom / Washroom", icon: ShowerHead },
  { value: "receiving", label: "Receiving / Goods-in", icon: Truck },
  { value: "office", label: "Office / Back of house", icon: Briefcase },
  { value: "other", label: "Other", icon: MoreHorizontal },
];

const CLEAN_AREAS = [
  { key: "production", label: "Production area" },
  { key: "restroom",   label: "Restroom"        },
  { key: "storage",    label: "Storage"         },
];

/**
 * Step 4 — Facilities (kitchens, cold rooms, washrooms, etc.).
 *
 * Each facility belongs to a site and carries the compliance toggles
 * that drive the Daily Hygiene + Daily Cleanliness modules.
 */
export default function Step4Facilities({ stepIndex, totalSteps, onAdvance, onBack, onFinishLater }) {
  const { notify } = useDialog();
  const [sites, setSites] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [team, setTeam] = useState([]);
  const [activeSite, setActiveSite] = useState("");
  const [draft, setDraft] = useState(makeBlankDraft);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  function makeBlankDraft() {
    return {
      name: "", location: "", category: "food_production",
      person_in_charge: "",
      daily_hygiene_enabled: true,
      daily_cleanliness_enabled: true,
      cleanliness_areas: ["production", "restroom", "storage"],
    };
  }

  const load = async () => {
    try {
      const [{ data: s }, { data: f }, { data: u }] = await Promise.all([
        api.get("/sites"),
        api.get("/facilities"),
        api.get("/users"),
      ]);
      setSites(s.sites || []);
      setFacilities(f.facilities || []);
      setTeam((u.users || []).filter((m) => !m.is_archived && m.role !== "super_admin"));
      if (!activeSite && s.sites?.length) setActiveSite(s.sites[0].id);
    } catch (e) { console.debug("step4 load failed", e); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // When category changes, sensible defaults for compliance toggles.
  useEffect(() => {
    if (draft.category === "food_production") {
      setDraft((d) => ({ ...d, daily_hygiene_enabled: true, daily_cleanliness_enabled: true }));
    } else if (draft.category === "office") {
      setDraft((d) => ({ ...d, daily_hygiene_enabled: false, daily_cleanliness_enabled: false }));
    } else {
      setDraft((d) => ({ ...d, daily_hygiene_enabled: false, daily_cleanliness_enabled: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.category]);

  const addFacility = async () => {
    setErr("");
    if (!activeSite) return setErr("Add a site first (Step 2b).");
    if (!draft.name.trim()) return setErr("Facility name is required.");
    setBusy(true);
    try {
      await api.post("/facilities", {
        site_id: activeSite,
        name: draft.name.trim(),
        location: draft.location.trim(),
        person_in_charge: draft.person_in_charge.trim(),
        category: draft.category,
        daily_hygiene_enabled: draft.daily_hygiene_enabled,
        daily_cleanliness_enabled: draft.daily_cleanliness_enabled,
        cleanliness_areas: draft.daily_cleanliness_enabled ? draft.cleanliness_areas : [],
      });
      setDraft(makeBlankDraft());
      await load();
    } catch (ex) {
      await notify({
        title: "Couldn't add facility",
        message: formatErr(ex.response?.data?.detail) || "Please try again.",
        variant: "err",
      });
    }
    finally { setBusy(false); }
  };

  const removeFacility = async (fid) => {
    setBusy(true); setErr("");
    try { await api.delete(`/facilities/${fid}`); await load(); }
    catch (ex) {
      await notify({
        title: "Couldn't remove facility",
        message: formatErr(ex.response?.data?.detail) || "Please try again.",
        variant: "err",
      });
    }
    finally { setBusy(false); }
  };

  // Edit modal — same shape as the add form but pre-populated. PUT /facilities/{id}
  // back-fills cleanliness_areas correctly so the cleanliness catch-up
  // template only asks for sub-areas the facility actually inspects.
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const openEdit = (f) => {
    setEditingId(f.id);
    setEditDraft({
      name: f.name || "",
      location: f.location || "",
      category: f.category || "food_production",
      person_in_charge: f.person_in_charge || "",
      daily_hygiene_enabled: !!f.daily_hygiene_enabled,
      daily_cleanliness_enabled: !!f.daily_cleanliness_enabled,
      cleanliness_areas: Array.isArray(f.cleanliness_areas)
        ? [...f.cleanliness_areas]
        : ["production", "restroom", "storage"],
      site_id: f.site_id,
    });
  };
  const closeEdit = () => { setEditingId(null); setEditDraft(null); };
  const toggleEditArea = (k) => {
    setEditDraft((d) => {
      const has = d.cleanliness_areas.includes(k);
      return { ...d, cleanliness_areas: has ? d.cleanliness_areas.filter((x) => x !== k) : [...d.cleanliness_areas, k] };
    });
  };
  const saveEdit = async () => {
    if (!editDraft) return;
    if (!editDraft.name.trim()) {
      return notify({ title: "Name required", message: "Facility name can't be empty.", variant: "err" });
    }
    setBusy(true);
    try {
      await api.put(`/facilities/${editingId}`, {
        site_id: editDraft.site_id,
        name: editDraft.name.trim(),
        location: editDraft.location.trim(),
        category: editDraft.category,
        person_in_charge: editDraft.person_in_charge.trim(),
        daily_hygiene_enabled: editDraft.daily_hygiene_enabled,
        daily_cleanliness_enabled: editDraft.daily_cleanliness_enabled,
        cleanliness_areas: editDraft.daily_cleanliness_enabled ? editDraft.cleanliness_areas : [],
      });
      closeEdit();
      await load();
    } catch (ex) {
      await notify({
        title: "Couldn't save changes",
        message: formatErr(ex.response?.data?.detail) || "Please try again.",
        variant: "err",
      });
    } finally { setBusy(false); }
  };

  const toggleArea = (k) => {
    setDraft((d) => {
      const has = d.cleanliness_areas.includes(k);
      return { ...d, cleanliness_areas: has ? d.cleanliness_areas.filter((x) => x !== k) : [...d.cleanliness_areas, k] };
    });
  };

  const visibleFacilities = facilities.filter((f) => f.site_id === activeSite);
  // PIC dropdown — team members assigned to the active site.
  const sitePicCandidates = team.filter((m) =>
    !m.site_ids || m.site_ids.length === 0 || m.site_ids.includes(activeSite),
  );

  return (
    <StepLayout
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      title="Map out your facilities"
      subtitle="A facility is a sub-area inside a site — a kitchen, cold room, restroom. Each gets its own daily checks."
      description={
        <div className="space-y-3">
          <p>Pick a site on the right, then add the facilities inside it. Toggles control which daily checks appear for staff.</p>
          <p className="text-xs opacity-70">Food-production facilities default to <strong>both</strong> hygiene & cleanliness; restrooms to cleanliness only; offices to neither.</p>
        </div>
      }
      onBack={onBack}
      onNext={() => onAdvance({ facilities: true })}
      onFinishLater={onFinishLater}
      busy={busy}
      errorMsg={err}
    >
      <div className="space-y-5">
        {/* Add facility card — site picker now lives INSIDE the card so the
            owner always knows which site they're adding to. */}
        <div className="vb-card p-6 space-y-3" data-testid="wiz-add-facility-card">
          <div className="flex items-center gap-2">
            <Building2 size={18} style={{ color: "var(--vb-text-2)" }} />
            <h2 className="vb-heading text-xl m-0" style={{ color: "var(--vb-text-2)" }}>Add a facility</h2>
          </div>

          {sites.length === 0 ? (
            <div className="vb-pill warn">
              No sites yet — go back to Step 2b to add one.
            </div>
          ) : (
            <div>
              <Label>Site *</Label>
              {sites.length === 1 ? (
                <div
                  className="vb-pill ok inline-flex"
                  style={{ fontSize: 14, padding: "8px 14px" }}
                  data-testid={`wiz-fac-site-${sites[0].id}`}>
                  <MapPin size={13} /> {sites[0].name}
                </div>
              ) : (
                <div className="flex gap-2 flex-wrap" data-testid="wiz-fac-site-switcher">
                  {sites.map((s) => (
                    <button key={s.id} type="button"
                      className={`vb-pill ${activeSite === s.id ? "ok" : ""}`}
                      style={{
                        cursor: "pointer",
                        fontSize: 14,
                        padding: "8px 14px",
                        border: activeSite === s.id ? undefined : "1px solid var(--vb-border)",
                      }}
                      onClick={() => setActiveSite(s.id)}
                      data-testid={`wiz-fac-site-${s.id}`}>
                      <MapPin size={13} /> {s.name} · {facilities.filter((f) => f.site_id === s.id).length}
                    </button>
                  ))}
                </div>
              )}
              <p className="text-[11px] opacity-60 mt-1.5 m-0">
                Pick the site this facility belongs to.
              </p>
            </div>
          )}

          <div>
            <Label>Category</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="wiz-fac-cat-grid">
              {CATEGORIES.map((c) => {
                const Icon = c.icon;
                const on = draft.category === c.value;
                return (
                  <button key={c.value} type="button"
                    onClick={() => setDraft({ ...draft, category: c.value })}
                    className="vb-card text-left p-2.5"
                    style={{
                      borderColor: on ? "var(--vb-accent)" : "var(--vb-border)",
                      borderWidth: 2,
                      background: on ? "var(--vb-bg-2)" : undefined,
                      cursor: "pointer",
                    }}
                    data-testid={`wiz-fac-cat-${c.value}`}>
                    <Icon size={16} style={{ color: on ? "var(--vb-accent)" : "var(--vb-text-2)" }} />
                    <div className="text-xs mt-1.5 leading-tight">{c.label}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Facility name *</Label>
              <input className="vb-input" value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="e.g. Lime Café Kitchen"
                data-testid="wiz-fac-name" />
            </div>
            <div>
              <Label>Location inside site</Label>
              <input className="vb-input" value={draft.location}
                onChange={(e) => setDraft({ ...draft, location: e.target.value })}
                placeholder="e.g. Level 2, East Wing"
                data-testid="wiz-fac-location" />
            </div>
            <div className="sm:col-span-2">
              <Label>Person in charge (PIC)</Label>
              <select className="vb-input" value={draft.person_in_charge}
                onChange={(e) => setDraft({ ...draft, person_in_charge: e.target.value })}
                data-testid="wiz-fac-pic">
                <option value="">— Select from team —</option>
                {sitePicCandidates.map((m) => (
                  <option key={m.id} value={m.name}>{m.name} ({m.role})</option>
                ))}
              </select>
              {sitePicCandidates.length === 0 && (
                <p className="text-[11px] opacity-60 mt-1 m-0">
                  No team members assigned to this site yet — go back to Step 3 to add some.
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t" style={{ borderColor: "var(--vb-border)" }}>
            <label className="flex items-start gap-2 cursor-pointer" data-testid="wiz-fac-hyg-toggle">
              <input type="checkbox" className="mt-1"
                checked={draft.daily_hygiene_enabled}
                onChange={(e) => setDraft({ ...draft, daily_hygiene_enabled: e.target.checked })} />
              <div>
                <div className="text-sm font-semibold" style={{ color: "var(--vb-text-2)" }}>Daily hygiene checks</div>
                <p className="text-[11px] opacity-60 m-0">For food-production facilities (VB-PRP-08).</p>
              </div>
            </label>
            <label className="flex items-start gap-2 cursor-pointer" data-testid="wiz-fac-clean-toggle">
              <input type="checkbox" className="mt-1"
                checked={draft.daily_cleanliness_enabled}
                onChange={(e) => setDraft({ ...draft, daily_cleanliness_enabled: e.target.checked })} />
              <div>
                <div className="text-sm font-semibold" style={{ color: "var(--vb-text-2)" }}>Daily cleanliness checks</div>
                <p className="text-[11px] opacity-60 m-0">For any facility that gets cleaned (VB-PRP-07).</p>
              </div>
            </label>
          </div>

          {draft.daily_cleanliness_enabled && (
            <div>
              <Label>Cleanliness sub-areas</Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5" data-testid="wiz-fac-clean-areas">
                {CLEAN_AREAS.map((a) => {
                  const on = draft.cleanliness_areas.includes(a.key);
                  return (
                    <label key={a.key}
                      className="flex items-center gap-2 cursor-pointer rounded p-2 hover:bg-[var(--vb-bg-2)]"
                      style={{ border: "1px solid var(--vb-border)" }}
                      data-testid={`wiz-fac-area-${a.key}`}>
                      <input type="checkbox" checked={on}
                        onChange={() => toggleArea(a.key)}
                        data-testid={`wiz-fac-area-${a.key}-cb`} />
                      <span className="text-sm">{a.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <button type="button" className="vb-btn" onClick={addFacility} disabled={busy}
              data-testid="wiz-fac-add-btn">
              <Plus size={14} /> Add facility
            </button>
          </div>
        </div>

        {/* Existing facilities */}
        {visibleFacilities.length === 0 ? (
          <div className="vb-card p-6 text-center text-sm opacity-70" data-testid="wiz-fac-empty">
            No facilities yet for this site — add one above.
          </div>
        ) : (
          <div className="vb-card p-0 overflow-hidden" data-testid="wiz-fac-list">
            <table className="vb-table">
              <thead><tr><th>Facility</th><th>Category</th><th>PIC</th><th>Checks</th><th></th></tr></thead>
              <tbody>
                {visibleFacilities.map((f) => {
                  const cat = CATEGORIES.find((c) => c.value === f.category);
                  return (
                    <tr key={f.id} data-testid={`wiz-fac-row-${f.id}`}>
                      <td>
                        <strong>{f.name}</strong>
                        {f.location && <div className="text-xs opacity-60">{f.location}</div>}
                      </td>
                      <td className="text-xs">{cat?.label || f.category}</td>
                      <td className="text-xs">{f.person_in_charge || "—"}</td>
                      <td className="text-xs">
                        {f.daily_hygiene_enabled && <span className="vb-pill ok text-[10px] mr-1">Hyg</span>}
                        {f.daily_cleanliness_enabled && <span className="vb-pill text-[10px]">Clean</span>}
                        {!f.daily_hygiene_enabled && !f.daily_cleanliness_enabled && <span className="opacity-50">—</span>}
                      </td>
                      <td className="text-right whitespace-nowrap">
                        <button type="button" className="vb-btn ghost sm mr-1"
                          onClick={() => openEdit(f)}
                          data-testid={`wiz-fac-edit-${f.id}`}>
                          <Pencil size={11} />
                        </button>
                        <button type="button" className="vb-btn ghost sm"
                          onClick={() => removeFacility(f.id)}
                          style={{ color: "var(--vb-error)" }}
                          data-testid={`wiz-fac-del-${f.id}`}>
                          <Trash2 size={11} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {editingId && editDraft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(60, 40, 24, 0.6)" }}
          data-testid="wiz-fac-edit-modal">
          <div className="vb-card max-w-lg w-full p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h2 className="vb-heading text-lg m-0" style={{ color: "var(--vb-text-2)" }}>Edit facility</h2>
              <button onClick={closeEdit} className="opacity-60 hover:opacity-100"
                data-testid="wiz-fac-edit-close"><XIcon size={18} /></button>
            </div>
            <div className="grid gap-3">
              <div>
                <Label>Category</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {CATEGORIES.map((c) => {
                    const Icon = c.icon;
                    const on = editDraft.category === c.value;
                    return (
                      <button key={c.value} type="button"
                        onClick={() => setEditDraft({ ...editDraft, category: c.value })}
                        className="vb-card text-left p-2"
                        style={{
                          borderColor: on ? "var(--vb-accent)" : "var(--vb-border)",
                          borderWidth: 2,
                          background: on ? "var(--vb-bg-2)" : undefined,
                          cursor: "pointer",
                        }}
                        data-testid={`wiz-fac-edit-cat-${c.value}`}>
                        <Icon size={14} style={{ color: on ? "var(--vb-accent)" : "var(--vb-text-2)" }} />
                        <div className="text-[11px] mt-1 leading-tight">{c.label}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label>Facility name *</Label>
                <input className="vb-input" value={editDraft.name}
                  onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                  data-testid="wiz-fac-edit-name" />
              </div>
              <div>
                <Label>Location inside site</Label>
                <input className="vb-input" value={editDraft.location}
                  onChange={(e) => setEditDraft({ ...editDraft, location: e.target.value })}
                  data-testid="wiz-fac-edit-location" />
              </div>
              <div>
                <Label>Person in charge (PIC)</Label>
                <select className="vb-input" value={editDraft.person_in_charge}
                  onChange={(e) => setEditDraft({ ...editDraft, person_in_charge: e.target.value })}
                  data-testid="wiz-fac-edit-pic">
                  <option value="">— Select from team —</option>
                  {team.filter((m) => !m.site_ids || m.site_ids.length === 0 || m.site_ids.includes(editDraft.site_id)).map((m) => (
                    <option key={m.id} value={m.name}>{m.name} ({m.role})</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3 pt-2 border-t" style={{ borderColor: "var(--vb-border)" }}>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" className="mt-1"
                    checked={editDraft.daily_hygiene_enabled}
                    onChange={(e) => setEditDraft({ ...editDraft, daily_hygiene_enabled: e.target.checked })}
                    data-testid="wiz-fac-edit-hyg-cb" />
                  <div>
                    <div className="text-xs font-semibold" style={{ color: "var(--vb-text-2)" }}>Daily hygiene</div>
                  </div>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" className="mt-1"
                    checked={editDraft.daily_cleanliness_enabled}
                    onChange={(e) => setEditDraft({ ...editDraft, daily_cleanliness_enabled: e.target.checked })}
                    data-testid="wiz-fac-edit-clean-cb" />
                  <div>
                    <div className="text-xs font-semibold" style={{ color: "var(--vb-text-2)" }}>Daily cleanliness</div>
                  </div>
                </label>
              </div>
              {editDraft.daily_cleanliness_enabled && (
                <div>
                  <Label>Cleanliness sub-areas</Label>
                  <div className="grid grid-cols-3 gap-1.5">
                    {CLEAN_AREAS.map((a) => {
                      const on = editDraft.cleanliness_areas.includes(a.key);
                      return (
                        <label key={a.key}
                          className="flex items-center gap-2 cursor-pointer rounded p-2"
                          style={{ border: on ? "2px solid var(--vb-accent)" : "1px solid var(--vb-border)" }}
                          data-testid={`wiz-fac-edit-area-${a.key}`}>
                          <input type="checkbox" checked={on}
                            onChange={() => toggleEditArea(a.key)}
                            data-testid={`wiz-fac-edit-area-${a.key}-cb`} />
                          <span className="text-xs">{a.label}</span>
                        </label>
                      );
                    })}
                  </div>
                  <p className="text-[10px] opacity-60 mt-1">
                    Drives which checklist sections appear in catch-up &amp; daily-tracking for this facility.
                  </p>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button type="button" className="vb-btn ghost" onClick={closeEdit} disabled={busy}
                data-testid="wiz-fac-edit-cancel">Cancel</button>
              <button type="button" className="vb-btn" onClick={saveEdit} disabled={busy}
                data-testid="wiz-fac-edit-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </StepLayout>
  );
}

function Label({ children }) {
  return (
    <label className="block text-[11px] font-semibold mb-1 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>
      {children}
    </label>
  );
}
